import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { useMenuStore } from '../stores/menuStore.js';
import { useOrdersStore } from '../stores/ordersStore.js';
import { LeftSidebar } from '../components/home/LeftSidebar.jsx';
import { MenuSearchHeader } from '../components/home/MenuSearchHeader.jsx';
import { MenuGridPanel } from '../components/home/MenuGridPanel.jsx';
import { RightSidebar } from '../components/home/RightSidebar.jsx';
import { DashboardPanel } from '../components/home/DashboardPanel.jsx';
import { MobileLayout } from '../components/mobile/MobileLayout.jsx';

export const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, isLoading, outletId, outletName, userName } = useAuth();
  const fetchMenus = useMenuStore((s) => s.fetchMenus);
  const { items, menus, selectedMenuId, setSelectedMenu, isLoading: menuLoading, cart } = useMenuStore();
  const fetchActiveOrders = useOrdersStore((s) => s.fetchActiveOrders);
  const [showDashboard, setShowDashboard] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const handleToggleDashboard = useCallback(() => {
    setShowDashboard((prev) => !prev);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const handleMenuChange = useCallback((menuId) => {
    setSelectedMenu(menuId);
    setSelectedCategory('All');
    setSearchQuery('');
  }, [setSelectedMenu]);

  // Derived: categories
  const categories = useMemo(() => {
    const catSet = new Set();
    items.forEach((item) => {
      (item.category || []).forEach((c) => {
        if (c && typeof c === 'string' && c.trim()) catSet.add(c.trim());
      });
      (item.category2 || []).forEach((c) => {
        if (c && typeof c === 'string' && c.trim()) catSet.add(c.trim());
      });
    });
    return ['All', ...Array.from(catSet).sort()];
  }, [items]);

  // Derived: filtered items
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesCategory =
        selectedCategory === 'All' ||
        (item.category || []).some(
          (c) => typeof c === 'string' && c.toLowerCase() === selectedCategory.toLowerCase()
        ) ||
        (item.category2 || []).some(
          (c) => typeof c === 'string' && c.toLowerCase() === selectedCategory.toLowerCase()
        );
      const matchesSearch =
        !searchQuery || item.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [items, selectedCategory, searchQuery]);

  // Bootstrap data on mount
  useEffect(() => {
    if (outletId) {
      fetchMenus(outletId);
      fetchActiveOrders(outletId);
    }
  }, [outletId, fetchMenus, fetchActiveOrders]);

  // Auth loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: '#EBEBEF',
          gap: 2,
        }}
      >
        <CircularProgress size={32} sx={{ color: '#4527A0' }} />
        <Typography variant="body2" sx={{ color: '#86868B', fontWeight: 500 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  // Not authenticated → redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // ═══════════════════════════════════════════════════════════════
  //  MOBILE LAYOUT — delegated to MobileLayout component
  // ═══════════════════════════════════════════════════════════════
  if (isMobile) {
    return <MobileLayout />;
  }

  // ═══════════════════════════════════════════════════════════════
  //  DESKTOP LAYOUT (Cloud Canvas — 3-column glassmorphic shell)
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        bgcolor: '#EBEBEF',
        p: '12px',
        gap: '12px',
      }}
    >
      {/* ─── Left Sidebar ─────────────────────────────────── */}
      <LeftSidebar
        showDashboard={showDashboard}
        onToggleDashboard={handleToggleDashboard}
        collapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
      />

      {/* ─── Main Workspace ────────────────────────────────── */}
      {showDashboard ? (
        <DashboardPanel onBack={() => setShowDashboard(false)} />
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden',
            bgcolor: 'rgba(255, 255, 255, 0.72)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            borderRadius: '20px',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
          }}
        >
          {/* Search + Categories header */}
          <MenuSearchHeader
            menus={menus}
            selectedMenuId={selectedMenuId}
            onMenuChange={handleMenuChange}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            outletName={outletName}
            userName={userName}
          />

          {/* Menu items grid */}
          <MenuGridPanel
            filteredItems={filteredItems}
            isLoading={menuLoading}
            searchQuery={searchQuery}
            hasItems={items.length > 0}
            onClearFilters={() => {
              setSearchQuery('');
              setSelectedCategory('All');
            }}
          />
        </Box>
      )}

      {/* ─── Right Sidebar — full height ───────────────────── */}
      {!showDashboard && <RightSidebar />}
    </Box>
  );
};
